import React from 'react';
import { useTheme } from '../../context/ThemeContext';

const Step3Additionals = ({ 
  selectedPlan, 
  paymentMode, 
  routerCount, 
  extraSellers, 
  onChangeExtraSellers, 
  pricing 
}) => {
  const { isDark } = useTheme();

  const planNames = {
    free: 'FREE',
    solo_vpn: 'SOLO VPN',
    solo_hotspot: 'SOLO HOTSPOT',
    solo_pppoe: 'SOLO PPPOE',
    hotspot_pppoe: 'HOTSPOT + PPPOE',
    todo_incluido: 'TODO INCLUIDO'
  };

  const modeLabels = {
    monthly: 'Mensual',
    semiannual: 'Semestral',
    annual: 'Anual',
    lifetime: 'Pago Único'
  };

  const getPrice = (mode) => {
    if (!pricing || selectedPlan === 'free') return 0;
    const planPricing = pricing[selectedPlan];
    if (!planPricing) return 0;
    
    const modeMap = {
      'monthly': 'monthly',
      'semiannual': 'semiannual',
      'annual': 'annual',
      'lifetime': 'lifetime'
    };
    
    return planPricing[modeMap[mode]] || 0;
  };

  const calculateTotal = () => {
    if (!pricing || selectedPlan === 'free') return 0;
    
    const basePrice = getPrice(paymentMode);
    
    // Routers adicionales (el plan incluye 1)
    const extraRouters = Math.max(0, routerCount - 1);
    const routerCost = extraRouters * (pricing.extra_router_cost || 0);
    
    // Vendedores adicionales (solo si el plan permite vendedores)
    const hasSellers = selectedPlan !== 'solo_vpn';
    const extraSellersCost = hasSellers ? Math.max(0, extraSellers) * (pricing.extra_seller_cost || 0) : 0;
    
    return basePrice + routerCost + extraSellersCost;
  };

  // Calcular routers adicionales
  const extraRouters = Math.max(0, routerCount - 1);
  const includedRouters = 1;
  
  // Detectar si el plan tiene vendedores
  const hasSellers = selectedPlan !== 'solo_vpn';

  return (
    <div>
      <div className="text-center mb-10">
        <h2 className={`text-4xl font-extrabold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Personaliza tu Plan
        </h2>
        <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Agrega vendedores adicionales según tus necesidades
        </p>
      </div>

      {/* Resumen de selección */}
      <div className={`rounded-xl p-6 mb-8 ${
        isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow-lg'
      }`}>
        <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Tu Selección
        </h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Plan:</span>
            <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {planNames[selectedPlan]}
            </span>
          </div>
          <div className="flex justify-between">
            <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Modalidad:</span>
            <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {modeLabels[paymentMode]}
            </span>
          </div>
        </div>
      </div>

      {/* Configuración de recursos */}
      <div className={`rounded-xl p-6 mb-8 ${
        isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow-lg'
      }`}>
        <h3 className={`text-xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Recursos del Plan
        </h3>

        {/* Routers (solo informativo) */}
        <div className="mb-6">
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Routers a Administrar
          </label>
          <div className={`p-4 rounded-lg ${
            isDark ? 'bg-gray-700' : 'bg-gray-100'
          }`}>
            <div className="flex justify-between items-center mb-2">
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Routers incluidos en el plan:
              </span>
              <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {includedRouters}
              </span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Routers adicionales:
              </span>
              <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {extraRouters}
              </span>
            </div>
            <div className={`border-t pt-2 mt-2 flex justify-between items-center ${
              isDark ? 'border-gray-600' : 'border-gray-300'
            }`}>
              <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Total de routers:
              </span>
              <span className={`text-lg font-bold text-blue-600`}>
                {routerCount}
              </span>
            </div>
          </div>
          <p className={`text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            💡 Este valor viene del número de routers que indicaste al registrarte
          </p>
        </div>

        {/* Vendedores adicionales (editable solo si el plan lo permite) */}
        <div>
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Vendedores Adicionales
          </label>
          <input
            type="number"
            min="0"
            value={hasSellers ? extraSellers : 0}
            onChange={(e) => hasSellers && onChangeExtraSellers(parseInt(e.target.value) || 0)}
            disabled={!hasSellers}
            className={`w-full px-4 py-3 rounded-lg border text-lg font-semibold ${
              isDark 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
            } ${!hasSellers ? 'opacity-50 cursor-not-allowed' : ''}`}
          />
          {hasSellers ? (
            <p className={`text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
              💡 El plan incluye {routerCount * 3} vendedores ({routerCount} router(s) × 3 vendedores cada uno)
            </p>
          ) : (
            <p className={`text-xs mt-2 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>
              ⚠️ Este plan no incluye gestión de vendedores
            </p>
          )}
        </div>
      </div>

      {/* Desglose de costos */}
      <div className={`rounded-xl p-6 ${
        isDark ? 'bg-gradient-to-br from-blue-900 to-purple-900' : 'bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-200'
      }`}>
        <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Desglose de Costos
        </h3>
        
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Plan {planNames[selectedPlan]} ({modeLabels[paymentMode]}):
            </span>
            <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              ${getPrice(paymentMode).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
            </span>
          </div>

          {extraRouters > 0 && (
            <div className="flex justify-between items-center">
              <span className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Routers adicionales ({extraRouters} × ${pricing?.extra_router_cost || 0}):
              </span>
              <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                ${(extraRouters * (pricing?.extra_router_cost || 0)).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
              </span>
            </div>
          )}

          {extraSellers > 0 && hasSellers && (
            <div className="flex justify-between items-center">
              <span className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Vendedores adicionales ({extraSellers} × ${pricing?.extra_seller_cost || 0}):
              </span>
              <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                ${(extraSellers * (pricing?.extra_seller_cost || 0)).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
              </span>
            </div>
          )}

          <div className={`border-t pt-3 mt-3 flex justify-between items-center ${
            isDark ? 'border-gray-600' : 'border-gray-300'
          }`}>
            <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              TOTAL:
            </span>
            <span className="text-3xl font-extrabold text-blue-600">
              ${calculateTotal().toLocaleString('es-MX', { minimumFractionDigits: 2 })}
            </span>
          </div>

          {paymentMode !== 'lifetime' && (
            <div className="text-center">
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {paymentMode === 'monthly' ? '/ mes' : paymentMode === 'semiannual' ? '/ 6 meses' : '/ año'}
              </span>
            </div>
          )}
        </div>
      </div>

      {selectedPlan !== 'free' && (
        <div className={`mt-6 p-4 rounded-xl text-center ${
          isDark ? 'bg-green-900/30 border border-green-700' : 'bg-green-100 border border-green-300'
        }`}>
          <p className={`text-sm font-medium ${isDark ? 'text-green-300' : 'text-green-800'}`}>
            🎉 Empezarás con <span className="font-bold">30 días GRATIS</span> para probar. Sin tarjeta de crédito requerida.
          </p>
        </div>
      )}
    </div>
  );
};

export default Step3Additionals;
